if (!customElements.get('acadian-fulfillment-portal')) {
  class AcadianFulfillmentPortal extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.list = this.querySelector('[data-afp-product-list]');
      if (!this.list) return;
      this.rows = Array.from(this.querySelectorAll('[data-afp-row]'));
      this.inputs = Array.from(this.querySelectorAll('[data-afp-quantity]'));
      this.search = this.querySelector('[data-afp-search]');
      this.vendor = this.querySelector('[data-afp-vendor]');
      this.category = this.querySelector('[data-afp-category]');
      this.availability = this.querySelector('[data-afp-availability]');
      this.sort = this.querySelector('[data-afp-sort]');
      this.visibleCount = this.querySelector('[data-afp-visible-count]');
      this.noResults = this.querySelector('[data-afp-no-results]');
      this.tray = this.querySelector('[data-afp-tray]');
      this.selectedItems = this.querySelector('[data-afp-selected-items]');
      this.selectedUnits = this.querySelector('[data-afp-selected-units]');
      this.dialog = this.querySelector('[data-afp-dialog]');
      this.reviewList = this.querySelector('[data-afp-review-list]');
      this.existingCart = this.querySelector('[data-afp-existing-cart]');
      this.existingMessage = this.querySelector('[data-afp-existing-message]');
      this.reviewButton = this.querySelector('[data-afp-review]');
      this.addButton = this.querySelector('[data-afp-add]');
      this.cartReady = false;
      this.checkedCart = null;
      this.status = this.querySelector('[data-afp-status]');
      this.success = this.querySelector('[data-afp-success]');
      this.addEventListener('click', (event) => this.handleClick(event));
      this.addEventListener('input', (event) => this.handleInput(event));
      this.addEventListener('change', (event) => this.handleChange(event));
      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      this.inputs.forEach((input) => this.updateStepper(input));
      this.applyFilters();
      this.updateSelection();
    }

    handleInput(event) {
      if (event.target.matches('[data-afp-search]')) {
        this.applyFilters();
        return;
      }
      if (event.target.matches('[data-afp-quantity]')) {
        this.updateSelection();
      }
    }

    handleChange(event) {
      if (event.target.matches('[data-afp-vendor], [data-afp-category], [data-afp-availability], [data-afp-sort]')) {
        this.applyFilters();
        return;
      }
      if (event.target.matches('[data-afp-quantity]')) {
        this.sanitizeQuantity(event.target);
        this.updateSelection();
      }
    }

    handleClick(event) {
      const stepButton = event.target.closest('[data-afp-minus], [data-afp-plus]');
      if (stepButton) {
        this.stepQuantity(stepButton);
        return;
      }
      if (event.target.closest('[data-afp-clear]')) {
        this.clearSelection();
        return;
      }
      if (event.target.closest('[data-afp-review]')) {
        this.openReview();
        return;
      }
      if (event.target.closest('[data-afp-dialog-close]')) {
        this.dialog?.close();
        return;
      }
      if (event.target.closest('[data-afp-add]')) {
        this.addToCart();
      }
    }

    sanitizeQuantity(input) {
      const minimum = Number(input.min || 0);
      const maximum = input.max ? Number(input.max) : 9999;
      let value = Number.parseInt(input.value, 10);
      if (!Number.isFinite(value)) value = minimum;
      input.value = Math.min(maximum, Math.max(minimum, value));
      this.updateStepper(input);
    }

    stepQuantity(button) {
      if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
      const stepper = button.closest('[data-afp-stepper]');
      const input = stepper?.querySelector('[data-afp-quantity]');
      if (!input || input.disabled) return;
      const delta = button.matches('[data-afp-plus]') ? 1 : -1;
      input.value = Number(input.value || 0) + delta;
      this.sanitizeQuantity(input);
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    updateStepper(input) {
      const stepper = input.closest('[data-afp-stepper]');
      if (!stepper) return;
      const minus = stepper.querySelector('[data-afp-minus]');
      const plus = stepper.querySelector('[data-afp-plus]');
      const value = Number(input.value || 0);
      const minimum = Number(input.min || 0);
      const maximum = input.max ? Number(input.max) : 9999;
      if (minus) {
        minus.disabled = input.disabled || value <= minimum;
        minus.setAttribute('aria-disabled', String(minus.disabled));
      }
      if (plus) {
        plus.disabled = input.disabled || value >= maximum;
        plus.setAttribute('aria-disabled', String(plus.disabled));
      }
    }

    applyFilters() {
      const query = (this.search?.value || '').trim().toLowerCase();
      const vendor = this.vendor?.value || '';
      const category = this.category?.value || '';
      const availability = this.availability?.value || '';
      const sort = this.sort?.value || 'featured';
      const sortedRows = [...this.rows].sort((a, b) => this.compareRows(a, b, sort));
      sortedRows.forEach((row) => this.list.appendChild(row));
      let shown = 0;
      sortedRows.forEach((row) => {
        const queryMatch = !query || row.dataset.search.includes(query);
        const vendorMatch = !vendor || row.dataset.vendor === vendor;
        const categoryMatch = !category || row.dataset.category === category;
        const availabilityMatch =
          !availability ||
          (availability === 'available' && row.dataset.available === 'true') ||
          (availability === 'unavailable' && row.dataset.available === 'false');
        const visible = queryMatch && vendorMatch && categoryMatch && availabilityMatch;
        row.hidden = !visible;
        if (visible) shown += 1;
      });
      if (this.visibleCount) {
        this.visibleCount.textContent = this.formatCount(this.dataset.resultsTemplate, shown);
      }
      if (this.noResults) this.noResults.hidden = shown !== 0;
    }

    compareRows(a, b, sort) {
      const text = (first, second) => first.localeCompare(second, undefined, { sensitivity: 'base', numeric: true });
      if (sort === 'title-asc') return text(a.dataset.title, b.dataset.title);
      if (sort === 'title-desc') return text(b.dataset.title, a.dataset.title);
      if (sort === 'sku-asc') return text(a.dataset.sku || 'zzzz', b.dataset.sku || 'zzzz');
      if (sort === 'available') {
        const stockDifference = Number(b.dataset.available === 'true') - Number(a.dataset.available === 'true');
        return stockDifference || text(a.dataset.title, b.dataset.title);
      }
      return Number(a.dataset.position) - Number(b.dataset.position);
    }

    selections() {
      return this.inputs
        .map((input) => ({
          input,
          id: Number(input.dataset.variantId),
          quantity: Number.parseInt(input.value, 10) || 0,
          title: input.dataset.title || '',
          variant: input.dataset.variant || '',
          sku: input.dataset.sku || ''
        }))
        .filter((item) => item.quantity > 0 && !item.input.disabled);
    }

    updateSelection() {
      this.inputs.forEach((input) => {
        this.updateStepper(input);
        input.closest('[data-afp-row]')?.classList.toggle('is-selected', Number(input.value || 0) > 0);
      });
      const selections = this.selections();
      const totalUnits = selections.reduce((total, item) => total + item.quantity, 0);
      if (this.selectedItems) {
        this.selectedItems.textContent = this.formatCount(this.dataset.selectedItemsTemplate, selections.length);
      }
      if (this.selectedUnits) {
        this.selectedUnits.textContent = this.formatCount(this.dataset.selectedUnitsTemplate, totalUnits);
      }
      if (this.tray) this.tray.hidden = selections.length === 0;
    }

    clearSelection() {
      this.inputs.forEach((input) => {
        input.value = 0;
        this.updateStepper(input);
      });
      if (this.success) this.success.hidden = true;
      this.updateSelection();
    }

    buildReview(selections) {
      if (!this.reviewList) return;
      this.reviewList.replaceChildren();
      selections.forEach((item) => {
        const line = document.createElement('li');
        const details = document.createElement('span');
        const title = document.createElement('strong');
        const meta = document.createElement('small');
        const quantity = document.createElement('strong');
        title.textContent = item.variant ? `${item.title} \u2014 ${item.variant}` : item.title;
        meta.textContent = item.sku ? `${this.dataset.skuLabel} ${item.sku}` : '';
        quantity.textContent = `\u00d7 ${item.quantity}`;
        details.append(title, meta);
        line.append(details, quantity);
        this.reviewList.appendChild(line);
      });
    }

    async openReview() {
      const selections = this.selections();
      if (!selections.length || !this.dialog) return;

      this.cartReady = false;
      this.checkedCart = null;
      if (this.addButton) this.addButton.disabled = true;
      if (this.existingCart) this.existingCart.hidden = true;
      if (this.reviewButton) {
        this.reviewButton.disabled = true;
        this.reviewButton.setAttribute('aria-busy', 'true');
      }

      try {
        const cart = await this.getCart();
        this.checkedCart = cart;
        this.cartReady = true;
        this.buildReview(selections);

        if (cart.item_count > 0 && this.existingCart && this.existingMessage) {
          this.existingMessage.textContent = this.formatCount(this.dataset.existingCartTemplate, cart.item_count);
          this.existingCart.hidden = false;
        }

        if (this.addButton) this.addButton.disabled = false;
        this.setStatus('', false);
        if (!this.dialog.open) this.dialog.showModal();
      } catch (error) {
        const message = error.message || this.dataset.cartError;
        this.setStatus(message, true);
        window.alert(message);
      } finally {
        if (this.reviewButton) {
          this.reviewButton.disabled = false;
          this.reviewButton.removeAttribute('aria-busy');
        }
      }
    }

    async getCart() {
      const response = await fetch(`${this.dataset.rootUrl}cart.js`, {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error(this.dataset.cartError);
      return response.json();
    }

    async normalizeMarker(cart, markerId) {
      const markerLines = cart.items.filter((item) => Number(item.variant_id) === markerId);
      if (!markerLines.length) return false;

      for (const [index, line] of markerLines.entries()) {
        const targetQuantity = index === 0 ? 1 : 0;
        if (Number(line.quantity) === targetQuantity) continue;

        const response = await fetch(`${this.dataset.rootUrl}cart/change.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ id: line.key, quantity: targetQuantity })
        });

        if (!response.ok) throw new Error(this.dataset.cartError);
      }

      return true;
    }

    async addToCart() {
      const selections = this.selections();
      if (!selections.length || !this.addButton) return;
      if (!this.cartReady) {
        this.setStatus(this.dataset.cartError, true);
        window.alert(this.dataset.cartError);
        return;
      }

      const markerId = Number(this.dataset.markerVariantId);
      if (!Number.isFinite(markerId) || markerId <= 0) {
        this.setStatus(this.dataset.markerError, true);
        return;
      }

      this.addButton.disabled = true;
      this.addButton.setAttribute('aria-busy', 'true');
      this.addButton.textContent = this.dataset.addingText;
      this.setStatus(this.dataset.addingText, false);

      try {
        const cart = await this.getCart();
        const markerExists = await this.normalizeMarker(cart, markerId);
        const items = selections.map((item) => ({
          id: item.id,
          quantity: item.quantity
        }));

        if (!markerExists) {
          items.push({
            id: markerId,
            quantity: 1
          });
        }

        const response = await fetch(`${this.dataset.rootUrl}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ items })
        });

        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.description || payload.message || this.dataset.addError);
        }

        this.cartReady = false;
        this.checkedCart = null;
        this.dialog?.close();
        this.inputs.forEach((input) => {
          input.value = 0;
          this.updateStepper(input);
        });
        this.updateSelection();

        if (this.success) {
          this.success.hidden = false;
          this.success.focus();
        }

        this.setStatus(this.dataset.successText, false);
      } catch (error) {
        const message = error.message || this.dataset.addError;
        this.cartReady = false;
        this.checkedCart = null;
        this.setStatus(message, true);
        window.alert(message);
      } finally {
        this.addButton.disabled = !this.cartReady;
        this.addButton.removeAttribute('aria-busy');
        this.addButton.textContent = this.addButton.dataset.defaultText;
      }
    }

    setStatus(message, isError) {
      if (!this.status) return;
      this.status.textContent = message || '';
      this.status.setAttribute('role', isError ? 'alert' : 'status');
    }

    formatCount(template, count) {
      return (template || '').replace('__COUNT__', String(count));
    }
  }

  customElements.define('acadian-fulfillment-portal', AcadianFulfillmentPortal);
}
